import { get, getHgproBaseurl, LogErrorMessage } from '../service';

/**
 * @typedef {import('../../types/location').LocationSearchResult} LocationSearchResult
 * @typedef {import('../../types/location').GeocodeApiResponse} GeocodeApiResponse
 * @typedef {import('../../types/location').AutosuggestWhereResponse} AutosuggestWhereResponse
 */

/**
 * Search for locations using the autosuggest/pr/where API
 * @param {string} searchTerm - The search term to find locations (city, state, etc.)
 * @param {string|undefined} [coordinates] - Optional coordinates in format "lat,lng"
 * @returns {Promise<LocationSearchResult[]>} Array of location results
 */
export const searchLocations = async (searchTerm, coordinates = undefined) => {
  if (!searchTerm || searchTerm.trim().length === 0) {
    return [];
  }

  const _url = getHgproBaseurl(window.location.hostname);

  // Build URL with term parameter
  let url = `${_url}/api/autosuggest/pr/where?term=${encodeURIComponent(searchTerm.trim())}`;

  // Add coordinates if provided
  if (coordinates) {
    url += `&pt=${encodeURIComponent(coordinates)}`;
  }

  try {
    /** @type {AutosuggestWhereResponse} */
    const response = await get(url);

    // Transform the nested response to simple {value, pt, city, state} format
    // Response structure: Response.Categories[].Suggestions[]
    const suggestions = response?.Response?.Categories?.[0]?.Suggestions || [];

    return suggestions.map((suggestion) => {
      const [fallbackCity, fallbackState] = suggestion.value
        .split(',')
        .map((s) => s.trim());

      return {
        value: suggestion.value,
        pt: suggestion.entity?.pt || '',
        city: suggestion.entity?.city || fallbackCity,
        state: suggestion.entity?.state || fallbackState,
      };
    });
  } catch (error) {
    console.error('Failed to search locations:', error);
    LogErrorMessage(error);
    return [];
  }
};

/**
 * Convert GPS coordinates to readable location (e.g., "Manhattan, NY")
 *
 * NOTE: Uses hardcoded healthgrades.com domain. Will fail locally due to CORS but works in production.
 * Not ideal, but we don't depend on this endpoint for significant functionality.
 *
 * @param {Object} coordinates
 * @param {number} coordinates.lat - Latitude
 * @param {number} coordinates.long - Longitude
 * @returns {Promise<GeocodeApiResponse|null>} Geocode result or null if failed
 */
export const geocodeLocation = async ({ lat, long }) => {
  const coordinates = `${lat},${long}`;

  // Hard-coded domain - do not change this!
  // This is the production HealthGrades geocoding endpoint
  // CORS Note: This will fail in local development due to cross-origin restrictions,
  // but works perfectly in production. The failures are expected and don't break
  // the app - users just see "Current Location" instead of the city name.
  const url = `https://www.healthgrades.com/api/geocode/pt?pt=${coordinates}`;

  try {
    const response = await get(url);
    return response;
  } catch (error) {
    console.warn(
      'Geocoding failed (expected in development due to CORS):',
      error,
    );
    LogErrorMessage(error);
    return null;
  }
};
